import { ArrowRight, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { ACTS, DISCLOSURES_ACT_INDEX } from './useDeckState';
import { T, FONT, SERIF, mix } from './theme';

interface Props {
  act: number;
  opt: number | null;
  optCount: number;
  go: (n: number) => void;
  advance: (dir: 1 | -1) => void;
  /** Wordmark text — advisor company/name, falls back to 'PlanScout'. */
  brandLabel: string;
  bookLabel: string;
  /** null when no booking URL — the Book CTA renders nothing. */
  onBook: (() => void) | null;
  onRefine: () => void;
}

/** Persistent footer: wordmark, Book CTA, refine link, progress dots, Back/Next. */
export function FooterNav({ act, opt, optCount, go, advance, brandLabel, bookLabel, onBook, onRefine }: Props) {
  const total = ACTS.length;
  const inPlan = act === 3 && opt !== null;
  const nextLabel = inPlan ? (opt === optCount - 1 ? 'See results' : 'Next optimization') : 'Next';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        background: T.card,
        borderTop: `1px solid ${T.grayLine}`,
        flexShrink: 0,
      }}
    >
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        alignItems: 'center',
        gap: 20,
        padding: '14px 28px',
      }}
    >
      {/* LEFT — wordmark + primary CTA + secondary CTA */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 18, minWidth: 0 }}>
        <div
          style={{
            paddingRight: 16,
            borderRight: `1px solid ${T.grayLine}`,
            display: 'flex',
            alignItems: 'center',
            lineHeight: 1.15,
          }}
        >
          <span
            style={{
              fontFamily: SERIF,
              fontSize: 18,
              fontWeight: 600,
              letterSpacing: '0.04em',
              color: T.navy,
              whiteSpace: 'nowrap',
            }}
          >
            {brandLabel}
          </span>
        </div>

        {/* Primary CTA — Book a follow-up (accent, most prominent) */}
        {onBook && (
          <button
            onClick={onBook}
            style={{
              padding: '10px 20px',
              borderRadius: 9,
              cursor: 'pointer',
              fontFamily: FONT,
              fontSize: 14,
              fontWeight: 700,
              border: 'none',
              background: T.teal,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              boxShadow: `0 3px 10px ${mix(T.teal, 28)}`,
              transition: 'transform .15s, box-shadow .15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = `0 6px 16px ${mix(T.teal, 38)}`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'none';
              e.currentTarget.style.boxShadow = `0 3px 10px ${mix(T.teal, 28)}`;
            }}
          >
            <Calendar size={16} /> {bookLabel}
          </button>
        )}

        {/* Secondary CTA — goes to Details in edit mode */}
        <button
          onClick={onRefine}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontFamily: FONT,
            fontSize: 13.5,
            fontWeight: 600,
            color: T.teal,
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            padding: '6px 4px',
          }}
        >
          Refine your plan <ArrowRight size={14} />
        </button>
      </div>

      {/* CENTER — progress + sub-progress */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          {Array.from({ length: total }).map((_, i) => (
            <div
              key={i}
              onClick={() => go(i)}
              title={ACTS[i]?.label}
              style={{
                width: i === act ? 20 : 7,
                height: 7,
                borderRadius: 4,
                background: i === act ? T.teal : i < act ? mix(T.teal, 45) : T.grayLine,
                cursor: 'pointer',
                transition: 'all .3s',
              }}
            />
          ))}
        </div>
        {inPlan && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 11, color: T.gray }}>
            <span>
              Optimization {(opt ?? 0) + 1} of {optCount}
            </span>
            <span style={{ display: 'flex', gap: 3 }}>
              {Array.from({ length: optCount }).map((_, i) => (
                <span
                  key={i}
                  style={{
                    width: 4,
                    height: 4,
                    borderRadius: 2,
                    background: i === opt ? T.teal : i < (opt ?? 0) ? mix(T.teal, 45) : T.grayLine,
                  }}
                />
              ))}
            </span>
          </div>
        )}
      </div>

      {/* RIGHT — Back / Next */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <NavBtn disabled={act === 0} onClick={() => advance(-1)} dir="back" />
        <NavBtn disabled={act === total - 1 && !inPlan} onClick={() => advance(1)} dir="next" label={nextLabel} />
      </div>
    </div>

    {/* Persistent footnote strip — compliance ambient on every screen */}
    <div
      style={{
        borderTop: `1px solid ${T.grayLine}`,
        padding: '5px 28px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
      }}
    >
      <span style={{ fontSize: 10.5, color: T.gray, lineHeight: 1.4 }}>
        This plan is a hypothetical illustration. Assumptions regarding investment returns and cost of living are not
        guarantees of future results. Not complete without all pages.
      </span>
      {act !== DISCLOSURES_ACT_INDEX && (
        <button
          onClick={() => go(DISCLOSURES_ACT_INDEX)}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontFamily: FONT,
            fontSize: 10.5,
            fontWeight: 600,
            color: T.gray,
            whiteSpace: 'nowrap',
            textDecoration: 'underline',
            textUnderlineOffset: 2,
            padding: 0,
            flexShrink: 0,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = T.navy)}
          onMouseLeave={(e) => (e.currentTarget.style.color = T.gray)}
        >
          View disclosures →
        </button>
      )}
    </div>
    </div>
  );
}

function NavBtn({
  disabled,
  onClick,
  dir,
  label,
}: {
  disabled?: boolean;
  onClick: () => void;
  dir: 'back' | 'next';
  label?: string;
}) {
  const text = label || (dir === 'back' ? 'Back' : 'Next');
  const isBack = dir === 'back';
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        padding: '9px 14px',
        borderRadius: 8,
        border: `1px solid ${disabled ? T.grayLine : isBack ? T.grayLine : T.navy}`,
        background: disabled ? 'transparent' : isBack ? 'transparent' : T.navy,
        color: disabled ? T.gray : isBack ? T.navy : '#fff',
        cursor: disabled ? 'default' : 'pointer',
        fontSize: 13,
        fontWeight: 600,
        fontFamily: FONT,
        opacity: disabled ? 0.45 : 1,
        transition: 'all .2s',
      }}
    >
      {isBack && <ChevronLeft size={16} />}
      {text}
      {!isBack && <ChevronRight size={16} />}
    </button>
  );
}
